import React from "react";
import { ExternalLink, Share2, Palette, Film, Quote } from "lucide-react";
import { openPublish } from "./MessageExtras";
import { Markdown } from "./Markdown";
import { mediaUrl } from "../lib/api";
import { MediaDriveSave } from "./MediaDriveSave";

const token = (p) => mediaUrl(`/files/${p}`);

function PublishBtn({ m, kind, idx }) {
  if (!m.path && !m.drive_id) return null;
  const media = { type: kind, name: m.name, ...(m.path ? { path: m.path } : { drive_id: m.drive_id }) };
  return (
    <button type="button" onClick={() => openPublish(media, m.title || m.prompt || "")} className="flex items-center gap-1 rounded-full bg-[#2F6BFF] px-2 py-0.5 font-semibold text-white transition-colors hover:bg-[#2558d6]" data-testid={`step-media-publish-${idx}`}>
      <Share2 size={10} /> Publikasikan
    </button>
  );
}

// Thumbnails of the attached reference images/videos a generated media item was steered by.
function StyleRefs({ refs, idx }) {
  if (!refs?.length) return null;
  return (
    <span className="flex items-center gap-1" title="Acuan gaya yang dipakai" data-testid={`step-media-style-refs-${idx}`}>
      <Palette size={11} className="text-[#7C3AED]" /><span className="text-[#7C3AED]">acuan gaya:</span>
      {refs.map((r, i) => r.kind === "image"
        ? <a key={i} href={token(r.path)} target="_blank" rel="noreferrer" title={r.name}><img src={token(r.path)} alt={r.name || ""} className="h-7 w-7 rounded-md border border-[#E7ECF3] object-cover ring-1 ring-[#7C3AED]/30" /></a>
        : <a key={i} href={token(r.path)} target="_blank" rel="noreferrer" title={r.name} className="flex h-7 w-7 items-center justify-center rounded-md border border-[#E7ECF3] bg-slate-50 text-[#7C3AED]"><Film size={12} /></a>)}
    </span>
  );
}

// The assistant's short caption — the name later steps use to refer to this image/video («lihat Gambar: …»).
function Caption({ m, idx }) {
  if (!m.caption) return null;
  return (
    <p className="mt-1.5 flex items-start gap-1.5 text-[13px] font-semibold text-slate-800" title="Caption — sebut nama ini di tahapan berikutnya" data-testid={`step-media-caption-${idx}`}>
      <Quote size={12} className="mt-0.5 shrink-0 text-[#2F6BFF]" /><span>{m.caption}</span>
    </p>
  );
}

export function StepMedia({ m, idx, taskId }) {
  if (m.kind === "video" || m.type === "video") {
    const src = m.drive_id ? mediaUrl(`/integrations/google/media/${m.drive_id}`) : m.path ? token(m.path) : null;
    return (
      <figure className="my-3" data-testid={`step-media-video-${idx}`}>
        {src && <video controls preload="metadata" className="max-h-72 w-full rounded-xl bg-black" src={src} />}
        <Caption m={m} idx={idx} />
        <figcaption className="mt-1 flex flex-wrap items-center gap-x-2 text-[11px] text-slate-500">
          <span>{m.model} · {m.duration}s · {m.resolution}</span>
          <StyleRefs refs={m.style_refs} idx={idx} />
          {m.path && !m.drive_id && <span className="text-amber-600">tersimpan di storage platform (30 hari)</span>}
          {taskId && <MediaDriveSave media={m} refId={`task:${taskId}:${m.path}`} compact />}
          {m.link && <a href={m.link} target="_blank" rel="noreferrer" className="flex items-center gap-0.5 text-[#2F6BFF] hover:underline"><ExternalLink size={10} /> Buka di Google Drive</a>}
          <PublishBtn m={m} kind="video" idx={idx} />
        </figcaption>
      </figure>
    );
  }
  if (!m.path) return <p className="my-2 text-xs text-slate-400">Gambar «{m.title}» sudah kedaluwarsa</p>;
  return (
    <figure className="my-3" data-testid={`step-media-image-${idx}`}>
      <img src={token(m.path)} alt={m.caption || m.title || ""} loading="lazy" className="max-h-96 w-full rounded-xl object-cover" />
      <Caption m={m} idx={idx} />
      <figcaption className="mt-1 flex flex-wrap items-center gap-2 text-[11px] text-slate-500">{m.title} · {m.model} · {m.aspect_ratio}<StyleRefs refs={m.style_refs} idx={idx} />{taskId && <MediaDriveSave media={m} refId={`task:${taskId}:${m.path}`} compact />}<PublishBtn m={m} kind="image" idx={idx} /></figcaption>
    </figure>
  );
}

// Markdown whose [[MEDIA:n]] tokens are replaced by the step's rendered images/videos (text + media in one deliverable).
export function MediaMarkdown({ content = "", media = [], taskId = null }) {
  const parts = (content || "").split(/\[\[MEDIA:(\d+)\]\]/);
  return (
    <div data-testid="media-markdown">
      {parts.map((p, i) => (i % 2 === 1 ? (media[Number(p)] ? <StepMedia key={i} m={media[Number(p)]} idx={Number(p)} taskId={taskId} /> : null) : p.trim() ? <Markdown key={i} content={p} /> : null))}
    </div>
  );
}
